import axiosV2Instance from './axiosV2Instance';

// Интерфейсы для платежей V2
export interface PaymentV2 {
  id: number;
  userId?: number;
  siteExecutorId?: number;
  siteId: number;
  user?: {
    id: number;
    name: string;
    closed: boolean;
  };
  amount: number;
  date: string;
  description?: string;
  signatureAdmin: boolean;
  signatureUser: boolean;
}

export interface PaymentV2Filters {
  startDate?: Date;
  endDate?: Date;
  userId?: number;
  siteExecutorId?: number;
  /** Ограничение количества (при отсутствии периода — последние N платежей, как в сменах) */
  limit?: number;
}

export interface UserV2 {
  id: number;
  name: string;
  closed: boolean;
}

export interface CreatePaymentV2Request {
  siteExecutorId?: number;
  userId?: number;
  amount: number;
  date: string;
  description?: string;
  comment?: string;
}

export interface PaymentWithRemainingV2 extends PaymentV2 {
  remainingAmount: number;
  /** Сумма в заголовке документа в журнале; `amount` здесь — пул по открытым строкам (как в распределении). */
  documentAmount?: number;
}

class PaymentsV2Api {
  /**
   * Получить доступные платежи для объектов, где у пользователя есть права:
   * - SESSION_EDIT (создание и редактирование смен)
   * - FINANCE_VIEW_DATA (просмотр финансовых данных)
   */
  async getAccessiblePayments(filters?: PaymentV2Filters): Promise<PaymentV2[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.startDate) {
        params.append('startDate', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        params.append('endDate', filters.endDate.toISOString());
      }
      if (filters?.userId) {
        params.append('userId', filters.userId.toString());
      }

      const url = `/PaymentsV2/accessible${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axiosV2Instance.get<PaymentV2[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching accessible payments:', error);
      throw error;
    }
  }

  /**
   * Получить платежи текущего пользователя (те, которые были ему сделаны)
   * Не требует проверки прав - любой пользователь может видеть свои платежи
   * Если период не указан, сервер вернет последние 30 платежей по умолчанию
   */
  async getUserPayments(filters?: PaymentV2Filters): Promise<PaymentV2[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.startDate) {
        params.append('startDate', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        params.append('endDate', filters.endDate.toISOString());
      }

      const url = `/PaymentsV2/my${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axiosV2Instance.get<PaymentV2[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching user payments:', error);
      throw error;
    }
  }

  /**
   * Получить список пользователей, которые состоят в объектах, где у текущего пользователя есть право FINANCE_VIEW_DATA
   * Используется для фильтра платежей
   */
  async getAccessibleUsers(): Promise<UserV2[]> {
    try {
      const response = await axiosV2Instance.get<UserV2[]>('/PaymentsV2/accessible-users');
      return response.data;
    } catch (error) {
      console.error('Error fetching accessible users:', error);
      throw error;
    }
  }

  /**
   * Получить платежи для конкретного объекта с учётом прав доступа (v2)
   * Пользователь должен быть участником объекта и иметь право FINANCE_VIEW_DATA
   */
  async getSitePayments(
    siteId: number,
    filters?: PaymentV2Filters
  ): Promise<PaymentV2[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.startDate) {
        params.append('startDate', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        params.append('endDate', filters.endDate.toISOString());
      }
      if (filters?.userId) {
        params.append('userId', filters.userId.toString());
      }
      if (filters?.siteExecutorId) {
        params.append('siteExecutorId', filters.siteExecutorId.toString());
      }
      if (filters?.limit != null && filters.limit > 0) {
        params.append('limit', filters.limit.toString());
      }

      const url = `/sites/${siteId}/payments${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axiosV2Instance.get<PaymentV2[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching site payments:', error);
      throw error;
    }
  }

  /**
   * Получить личные платежи пользователя для конкретного объекта
   * Не требует проверки прав - любой пользователь может видеть свои платежи на объекте
   */
  async getMySitePayments(
    siteId: number,
    filters?: Omit<PaymentV2Filters, 'userId' | 'siteExecutorId'>
  ): Promise<PaymentV2[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.startDate) {
        params.append('startDate', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        params.append('endDate', filters.endDate.toISOString());
      }
      if (filters?.limit != null && filters.limit > 0) {
        params.append('limit', filters.limit.toString());
      }

      const url = `/sites/${siteId}/payments/my${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axiosV2Instance.get<PaymentV2[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching my site payments:', error);
      throw error;
    }
  }

  /**
   * Проверить права пользователя на редактирование финансов объекта
   */
  async checkEditPermission(siteId: number): Promise<boolean> {
    try {
      const response = await axiosV2Instance.get(
        `/sites/${siteId}/payments/permissions/edit`
      );
      return response.data.canEdit;
    } catch (error) {
      console.error('Error checking edit permission:', error);
      return false;
    }
  }

  /**
   * Создать новый платеж сотруднику
   */
  async createPayment(
    siteId: number,
    payment: CreatePaymentV2Request
  ): Promise<number> {
    try {
      const response = await axiosV2Instance.post(
        `/sites/${siteId}/payments`,
        payment
      );
      return response.data.id;
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  }

  /**
   * Платежи с остатком по документу (в т.ч. отрицательным при перераспределении).
   * Сумма remainingAmount по списку = нетто нераспределено, согласовано с балансом.
   */
  async getSitePaymentsWithRemaining(
    siteId: number,
    filters?: PaymentV2Filters
  ): Promise<PaymentWithRemainingV2[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.startDate) {
        params.append('startDate', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        params.append('endDate', filters.endDate.toISOString());
      }
      if (filters?.userId) {
        params.append('userId', filters.userId.toString());
      }
      if (filters?.siteExecutorId) {
        params.append('siteExecutorId', filters.siteExecutorId.toString());
      }

      const url = `/sites/${siteId}/payments/with-remaining${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axiosV2Instance.get<PaymentWithRemainingV2[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching payments with remaining:', error);
      throw error;
    }
  }
}

export const paymentsV2Api = new PaymentsV2Api();

